import { useState, useEffect, useCallback, RefObject, useRef } from 'react';
import { ArrowDown } from 'lucide-react';
import '../Styles/Layout/Buttons/ScrollToBottom.css';
import { useWindow } from '../contexts/WindowContext';
import { ConvexMessage } from '../types/message';

interface ScrollHandlerResult {
  ScrollToBottomButton: () => React.ReactElement | null;
  scrollToBottom: () => void;
  scrollToBottomGenerous: () => void;
  Spacer: () => React.ReactElement | null;
}

interface UseScrollHandlerProps {
  messagesContainerRef: RefObject<HTMLDivElement>;
  isGeneratingResponse: boolean;
  chatId?: string;
  messages?: ConvexMessage[];
}

const useScrollHandler = ({
  messagesContainerRef,
  isGeneratingResponse,
  chatId,
  messages
}: UseScrollHandlerProps): ScrollHandlerResult => {
  const [showScrollButton, setShowScrollButton] = useState<boolean>(false);
  const [showSpacer, setShowSpacer] = useState<boolean>(false);
  const [spacerHeight, setSpacerHeight] = useState<string>('20vh');
  const [userScrolledUp, setUserScrolledUp] = useState<boolean>(false);
  const { windowWidth } = useWindow();
  const hasRenderableMessages = Boolean(messages?.length);
  const isNewOrEmptyChat = !chatId || chatId === 'new' || chatId === 'undefined' || !hasRenderableMessages;
  const isScrollingRef = useRef<boolean>(false);
  const prevIsGeneratingResponse = useRef<boolean>(isGeneratingResponse);
  const rafIdRef = useRef<number | null>(null);
  const initialSpacerVhRef = useRef<number>(0);
  const lastSpacerHeightPxRef = useRef<number>(0);
  const isInitialized = useRef(false);
  const userScrolledUpRef = useRef<boolean>(userScrolledUp);
  const generationScrollTimerRef = useRef<number | null>(null);
  const generationStateTimerRef = useRef<number | null>(null);
  const generationSpacerTimerRef = useRef<number | null>(null);

  useEffect(() => {
    userScrolledUpRef.current = userScrolledUp;
  }, [userScrolledUp]);

  const clearGenerationTimers = useCallback(() => {
    if (generationScrollTimerRef.current !== null) {
      window.clearTimeout(generationScrollTimerRef.current);
      generationScrollTimerRef.current = null;
    }

    if (generationStateTimerRef.current !== null) {
      window.clearTimeout(generationStateTimerRef.current);
      generationStateTimerRef.current = null;
    }

    if (generationSpacerTimerRef.current !== null) {
      window.clearTimeout(generationSpacerTimerRef.current);
      generationSpacerTimerRef.current = null;
    }
  }, []);

  const resetScrollUiState = useCallback(() => {
    clearGenerationTimers();

    if (rafIdRef.current !== null) {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = null;
    }

    isScrollingRef.current = false;
    userScrolledUpRef.current = false;
    initialSpacerVhRef.current = 0;
    lastSpacerHeightPxRef.current = 0;
    setShowScrollButton(false);
    setShowSpacer(false);
    setSpacerHeight('0px');
    setUserScrolledUp(false);
  }, [clearGenerationTimers]);

  useEffect(() => {
    return () => {
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }
      clearGenerationTimers();
    };
  }, [clearGenerationTimers]);

  useEffect(() => {
    if (isNewOrEmptyChat) {
      resetScrollUiState();
    }
  }, [isNewOrEmptyChat, resetScrollUiState]);

  const getSpacerSafeZoneHeight = useCallback(() => {
    const container = messagesContainerRef.current;
    const spacerElement = container?.querySelector<HTMLElement>('.scroll-spacer');

    if (spacerElement) {
      return spacerElement.getBoundingClientRect().height;
    }

    if (!showSpacer) return 0;

    const numericHeight = parseFloat(spacerHeight);
    if (Number.isNaN(numericHeight)) return 0;
    if (spacerHeight.endsWith('vh')) return window.innerHeight * (numericHeight / 100);
    return numericHeight;
  }, [messagesContainerRef, showSpacer, spacerHeight]);
  
  const checkIfNearBottom = useCallback(() => {
    const container = messagesContainerRef.current;
    if (!container) return true;
    const threshold = Math.min(200, Math.max(80, Math.floor(container.clientHeight * 0.2)));
    const distanceFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
    const effectiveDistanceFromBottom = Math.max(0, distanceFromBottom - getSpacerSafeZoneHeight());
    return effectiveDistanceFromBottom <= threshold;
  }, [messagesContainerRef, getSpacerSafeZoneHeight]);

  const updateScrollStateFromPosition = useCallback(() => {
    const isNear = checkIfNearBottom();
    setShowScrollButton(!isNear);
    setUserScrolledUp(!isNear);
    userScrolledUpRef.current = !isNear;
    return isNear;
  }, [checkIfNearBottom]);

  const updateSpacerBasedOnOutOfView = useCallback(() => {
    if (!showSpacer) return;
    const container = messagesContainerRef.current;
    if (!container) return;
    if (userScrolledUpRef.current) return;
    const distanceFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
    if (distanceFromBottom <= 0) return;
    const initialVh = initialSpacerVhRef.current;
    if (!initialVh || initialVh <= 0) return;
    const initialPx = Math.round(window.innerHeight * (initialVh / 100));
    const defaultPx = Math.round(window.innerHeight * 0.2); // 20vh
    const targetPx = Math.max(defaultPx, Math.min(initialPx, initialPx - Math.floor(distanceFromBottom)));
    if (Math.abs(targetPx - lastSpacerHeightPxRef.current) >= 2) {
      lastSpacerHeightPxRef.current = targetPx;
      setSpacerHeight(`${targetPx}px`);
    }
  }, [messagesContainerRef, showSpacer]);
  
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;
    const handleScroll = () => {
      if (isScrollingRef.current) return;
      updateScrollStateFromPosition();
      updateSpacerBasedOnOutOfView();
    };
    handleScroll();
    container.addEventListener('scroll', handleScroll);
    return () => {
      container.removeEventListener('scroll', handleScroll);
    };
  }, [messagesContainerRef, chatId, updateScrollStateFromPosition, updateSpacerBasedOnOutOfView]);
  
  const smoothScrollTo = useCallback((targetScrollTop: number, duration: number = 300) => {
    const container = messagesContainerRef.current;
    if (!container) return;

    if (rafIdRef.current !== null) {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = null;
    }

    isScrollingRef.current = true;
    setUserScrolledUp(false);
    const startScrollTop = container.scrollTop;
    const distance = targetScrollTop - startScrollTop;
    if (Math.abs(distance) < 1) {
      isScrollingRef.current = false;
      updateScrollStateFromPosition();
      return;
    }
    let startTime: number | null = null;

    const animationStep = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const elapsed = timestamp - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const easedProgress = 1 - Math.pow(1 - progress, 3);

      container.scrollTop = startScrollTop + distance * easedProgress;

      if (progress < 1) {
        rafIdRef.current = requestAnimationFrame(animationStep);
      } else {
        container.scrollTop = targetScrollTop;
        rafIdRef.current = null;
        isScrollingRef.current = false;
        updateScrollStateFromPosition();
        updateSpacerBasedOnOutOfView();
      }
    };
    rafIdRef.current = requestAnimationFrame(animationStep);
  }, [messagesContainerRef, updateScrollStateFromPosition, updateSpacerBasedOnOutOfView]);

  const scrollToBottom = useCallback((): void => {
    const container = messagesContainerRef.current;
    if (container) {
        const targetScrollTop = container.scrollHeight - container.clientHeight;
        smoothScrollTo(targetScrollTop);
    }
  }, [messagesContainerRef, smoothScrollTo]);

  const scrollToBottomGenerous = useCallback((): void => {
    const container = messagesContainerRef.current;
    if (container && !isScrollingRef.current) {
      const initialSpacerVh = windowWidth > 500 ? 50 : 40;
      const initialSpacerHeightString = `${initialSpacerVh}vh`;
      setShowSpacer(true);
      setSpacerHeight(initialSpacerHeightString);
      initialSpacerVhRef.current = initialSpacerVh;
      lastSpacerHeightPxRef.current = Math.round(window.innerHeight * (initialSpacerVh / 100));

      setTimeout(() => {
        if (messagesContainerRef.current) {
          const targetScrollTop = messagesContainerRef.current.scrollHeight - messagesContainerRef.current.clientHeight;
          smoothScrollTo(targetScrollTop, 500);
          setTimeout(() => {
            updateSpacerBasedOnOutOfView();
          }, 150);
        }
      }, 50);
    }
  }, [messagesContainerRef, windowWidth, smoothScrollTo, updateSpacerBasedOnOutOfView]);

  useEffect(() => {
    if (isInitialized.current) return;

    setShowSpacer(false);
    setSpacerHeight('0px');
    isScrollingRef.current = false;

    const container = messagesContainerRef.current;
    if (!container) return;

    const updateScrollState = () => {
        if (messagesContainerRef.current && !isScrollingRef.current) {
            updateScrollStateFromPosition();
        }
    };

    if (chatId && chatId !== 'new' && chatId !== 'undefined' && messages && messages.length > 0) {
      const timer = setTimeout(() => {
        if (!userScrolledUpRef.current && messagesContainerRef.current) {
                 scrollToBottom();
                 setTimeout(updateScrollState, 350);
        } else {
             updateScrollState();
        }
        isInitialized.current = true;
      }, 150);

      return () => clearTimeout(timer);

    } else if (!chatId || chatId === 'new' || chatId === 'undefined') {
      scrollToBottom();
      const stateTimer = setTimeout(() => {
           if (messagesContainerRef.current) {
              setShowScrollButton(false);
              setUserScrolledUp(false);
           }
           isInitialized.current = true;
      }, 350);
      return () => clearTimeout(stateTimer);
    }

    return () => {
      if (isScrollingRef.current) {
        isScrollingRef.current = false;
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatId, messagesContainerRef, messages?.length, updateScrollStateFromPosition]);

  // Reset initialization when chatId changes
  useEffect(() => {
    isInitialized.current = false;
  }, [chatId]);

  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    const generationStarted = isGeneratingResponse && !prevIsGeneratingResponse.current;
    const generationEnded = !isGeneratingResponse && prevIsGeneratingResponse.current;

    const updateScrollStateAfterGeneration = () => {
        if (messagesContainerRef.current && !isScrollingRef.current) {
            updateScrollStateFromPosition();
        }
    };

    if (generationStarted) {
        clearGenerationTimers();
        const initialSpacerVh = windowWidth > 500 ? 50 : 40;
        const initialSpacerHeightString = `${initialSpacerVh}vh`;
        setShowSpacer(true);
        setSpacerHeight(initialSpacerHeightString);
        initialSpacerVhRef.current = initialSpacerVh;
        lastSpacerHeightPxRef.current = Math.round(window.innerHeight * (initialSpacerVh / 100));

        generationScrollTimerRef.current = window.setTimeout(() => {
            generationScrollTimerRef.current = null;
            if (messagesContainerRef.current) {
                const targetScrollTop = messagesContainerRef.current.scrollHeight - messagesContainerRef.current.clientHeight;
                smoothScrollTo(targetScrollTop, 500);
                generationStateTimerRef.current = window.setTimeout(() => {
                    generationStateTimerRef.current = null;
                    updateScrollStateAfterGeneration();
                }, 550);
                generationSpacerTimerRef.current = window.setTimeout(() => {
                    generationSpacerTimerRef.current = null;
                    updateSpacerBasedOnOutOfView();
                }, 150);
            }
        }, 50);

    } else if (generationEnded) {
        // Finishing a response must not reconcile or reposition the viewport.
        // Cancel any delayed generation-start work and leave scroll/UI state
        // exactly where the user put it; the normal scroll listener will
        // update controls on the next actual user scroll.
        clearGenerationTimers();
        if (rafIdRef.current !== null) {
          cancelAnimationFrame(rafIdRef.current);
          rafIdRef.current = null;
        }
        isScrollingRef.current = false;
    }

    prevIsGeneratingResponse.current = isGeneratingResponse;

  }, [
    isGeneratingResponse,
    messagesContainerRef,
    windowWidth,
    smoothScrollTo,
    updateSpacerBasedOnOutOfView,
    updateScrollStateFromPosition,
    clearGenerationTimers
  ]);

  useEffect(() => {
    if (!showSpacer || !isGeneratingResponse) return;
    const interval = setInterval(() => {
      updateSpacerBasedOnOutOfView();
    }, 150);
    return () => clearInterval(interval);
  }, [showSpacer, isGeneratingResponse, updateSpacerBasedOnOutOfView]);

  const ScrollToBottomButton = useCallback(() => {
    if (!showScrollButton || isNewOrEmptyChat) return null;
    
    return (
      <button 
        type="button"
        className={`scroll-to-bottom ${isGeneratingResponse ? 'generating-response' : ''}`}
        onClick={() => {
          setUserScrolledUp(false);
          scrollToBottom();
        }} 
        aria-label="Scroll to bottom"
      >
        <ArrowDown size={20} aria-hidden />
      </button>
    );
  }, [showScrollButton, isNewOrEmptyChat, isGeneratingResponse, scrollToBottom]);

  const Spacer = useCallback(() => {
    if (!showSpacer) return null;
    
    return (
      <div 
        className="scroll-spacer"
        style={{ 
          height: spacerHeight, 
          width: '100%',
          transition: 'height 0.8s cubic-bezier(0.4, 0, 0.2, 1)',
          overflow: 'hidden'
        }} 
        onTransitionEnd={(e) => {
          if (e.propertyName === 'height') {
             const targetHeightPx = parseFloat(spacerHeight);

             if (!isNaN(targetHeightPx) && targetHeightPx < 1) {
                setShowSpacer(false);
             } else if (spacerHeight === '0px' || spacerHeight === '0') {
                 setShowSpacer(false);
             }
          }
        }}
        aria-hidden="true"
      />
    );
  }, [showSpacer, spacerHeight]);

  return { ScrollToBottomButton, scrollToBottom, scrollToBottomGenerous, Spacer };
};

export default useScrollHandler;
